// app/apps/campsite-explorer/components/MobileCTABar.tsx
'use client';

import React, { useEffect, useState } from 'react';
import { FaStar, FaPhone } from 'react-icons/fa';
import { Entity } from '../types';

function trackBookingClick(
  clickType: 'booking_link' | 'phone',
  entityId?: number,
  entityName?: string,
  campsiteType?: string,
) {
  try {
    const payload = JSON.stringify({
      clickType,
      entityId,
      entityName: entityName || '',
      campsiteType: campsiteType || '',
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        'https://app.trip-planner.com.au/api/products/track-booking-click',
        new Blob([payload], { type: 'application/json' }),
      );
    } else {
      fetch('https://app.trip-planner.com.au/api/products/track-booking-click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch {
    // Never block the user
  }
}

interface MobileCTABarProps {
  campsite: Entity;
}

export default function MobileCTABar({ campsite }: MobileCTABarProps) {
  const address = campsite.addresses?.[0];
  const hasBooking = !!campsite.booking_url;
  const hasPhone = !!address?.phone;
  const hasDirections = !!(address?.gps_latitude && address?.gps_longitude);
  const isFreeCamp = campsite.entity_type === 'free_camping';
  // Free camps and other unbookable sites still get a bar — "Directions" is
  // their booking moment. Only bail when there's truly nothing actionable.
  const showBar = hasBooking || hasPhone || hasDirections;

  // Signal to other fixed-bottom elements (e.g. TripChat) that the CTA bar is visible
  useEffect(() => {
    if (!showBar) return;
    document.documentElement.setAttribute('data-mobile-cta', 'true');
    return () => {
      document.documentElement.removeAttribute('data-mobile-cta');
    };
  }, [showBar]);

  // Past the hero + at-a-glance block? Only the ca_preview skin acts on this
  // (store PDPs reveal their sticky buy bar once the buy box scrolls away);
  // without the flag the bar renders exactly as before.
  const [scrolledPast, setScrolledPast] = useState(false);
  useEffect(() => {
    if (!showBar) return;
    // "Past" = the row right under the hero has scrolled up out of view. (The
    // hero itself swaps its DOM after mount, so it is not a stable target.)
    const hero = document.querySelector('.camp-detail > .max-w-7xl');
    if (hero && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([e]) => {
        setScrolledPast(!e.isIntersecting && e.boundingClientRect.top < 0);
      });
      io.observe(hero);
      return () => io.disconnect();
    }
    const onScroll = () => setScrolledPast(window.scrollY > 560);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [showBar]);
  useEffect(() => {
    const el = document.documentElement;
    if (showBar && scrolledPast) el.setAttribute('data-mobile-cta-shown', '');
    else el.removeAttribute('data-mobile-cta-shown');
    return () => el.removeAttribute('data-mobile-cta-shown');
  }, [showBar, scrolledPast]);

  if (!showBar) return null;

  const handleBookClick = () => {
    if (hasBooking) {
      trackBookingClick('booking_link', campsite.id, campsite.name, campsite.item);
      window.open(campsite.booking_url!, '_blank');
    }
  };

  const handlePhoneClick = () => {
    if (hasPhone) {
      trackBookingClick('phone', campsite.id, campsite.name, campsite.item);
      window.open(`tel:${address!.phone}`, '_self');
    }
  };

  const handleDirectionsClick = () => {
    if (hasDirections) {
      window.open(
        `https://www.google.com/maps/dir/?api=1&destination=${address!.gps_latitude},${address!.gps_longitude}`,
        '_blank',
      );
    }
  };

  // Only show on mobile
  return (
    <div className={`mobile-cta-bar${scrolledPast ? ' is-shown' : ''} md:hidden fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40 safe-area-inset-bottom`}>
      <div className="flex items-center justify-between p-4">
        <div className="min-w-0 flex-1 mr-3">
          <p className="font-semibold text-lg truncate">{campsite.name}</p>
          {campsite.rating ? (
            <div className="flex items-center text-sm text-gray-600">
              <FaStar className="text-yellow-500 mr-1" />
              <span>{campsite.rating}/5</span>
              {isFreeCamp && <span className="ml-2 font-semibold text-emerald-600">Free</span>}
            </div>
          ) : isFreeCamp ? (
            <p className="text-sm font-semibold text-emerald-600">Free · no booking needed</p>
          ) : null}
        </div>
        <div className="flex gap-2 shrink-0">
          {hasPhone && (
            <button
              onClick={handlePhoneClick}
              className="p-3 bg-gray-100 rounded-lg"
              aria-label="Call campsite"
            >
              <FaPhone />
            </button>
          )}
          {hasBooking && (
            <button
              onClick={handleBookClick}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold"
            >
              Book Now
            </button>
          )}
          {!hasBooking && hasPhone && (
            <button
              onClick={handlePhoneClick}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold"
            >
              Call Now
            </button>
          )}
          {!hasBooking && !hasPhone && hasDirections && (
            <button
              onClick={handleDirectionsClick}
              className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold"
            >
              Directions
            </button>
          )}
        </div>
      </div>

      <style jsx>{`
        .safe-area-inset-bottom {
          padding-bottom: env(safe-area-inset-bottom);
        }
      `}</style>
    </div>
  );
}